import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { MailSearch } from 'lucide-react';
import { useAccountFilter } from '../lib/accountFilter';
import { useBreakpoint, useDebounced, useHotkeys, useLocalStorage, useResizable } from '../hooks/useBrowser';
import { useFolders, useMailboxes } from '../hooks/useMailboxes';
import { useMe, useSettings } from '../hooks/useMe';
import { useCategoryMap } from '../hooks/useCategories';
import { useThreadList, useUpdateThread, type ApiView, type ThreadDetail, type ThreadFilter } from '../hooks/useThreads';
import { ThreadList } from '../components/mail/ThreadList';
import { ThreadView, type ThreadViewHandle } from '../components/mail/ThreadView';
import { ContextPanel } from '../components/mail/ContextPanel';
import { Drawer } from '../components/ui/Overlay';
import { EmptyState } from '../components/ui/Feedback';

export type MailView = 'inbox' | 'needs-reply' | 'flagged' | 'drafts' | 'sent' | 'folder';

const API_VIEW: Record<MailView, ApiView> = { inbox: 'inbox', 'needs-reply': 'needs_reply', flagged: 'flagged', drafts: 'drafts', sent: 'sent', folder: 'folder' };
const TITLES: Record<MailView, string> = { inbox: 'Inbox', 'needs-reply': 'Needs reply', flagged: 'Flagged', drafts: 'Drafts', sent: 'Sent', folder: 'Folder' };
const EMPTY: Record<MailView, string> = {
  inbox: 'New mail will appear here as your mailboxes sync.',
  'needs-reply': 'Nothing is waiting on you. Conversations where you owe a reply show up here.',
  flagged: 'Flag a conversation to keep it here.',
  drafts: 'Saved and suggested drafts appear here.',
  sent: 'Messages you send appear here.',
  folder: 'This folder is empty.',
};

function Divider({ onPointerDown, onKeyDown, label, dragging }: { onPointerDown: (e: React.PointerEvent) => void; onKeyDown: (e: React.KeyboardEvent) => void; label: string; dragging: boolean }) {
  return (
    <div role="separator" aria-orientation="vertical" aria-label={label} tabIndex={0} onPointerDown={onPointerDown} onKeyDown={onKeyDown} className="group relative z-10 w-3 shrink-0 cursor-col-resize touch-none">
      <span className={clsx('absolute inset-y-[40%] left-1/2 w-1 -translate-x-1/2 rounded-full bg-line opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100', dragging && '!bg-primary opacity-100')} />
    </div>
  );
}

export function MailPage({ view }: { view: MailView }) {
  const { threadId, folderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const bp = useBreakpoint();
  const { accountId, setAccountId } = useAccountFilter();
  const mbs = useMailboxes();
  const mailboxList = mbs.data?.mailboxes ?? [];
  const mailboxMap = useMemo(() => new Map(mailboxList.map((m) => [m.id, m])), [mailboxList]);

  // ?account= in the URL overrides / sets the global account filter
  useEffect(() => {
    const a = params.get('account');
    if (a !== null) {
      setAccountId(a === 'all' ? null : a);
      params.delete('account');
      setParams(params, { replace: true });
    }
  }, [params, setParams, setAccountId]);
  // drop a stale account filter
  useEffect(() => {
    if (accountId && mbs.isSuccess && !mailboxMap.has(accountId)) setAccountId(null);
  }, [accountId, mbs.isSuccess, mailboxMap, setAccountId]);

  const [search, setSearch] = useState(params.get('q') ?? '');
  // A search started elsewhere (command palette) arrives as ?q=
  const urlQ = params.get('q');
  useEffect(() => {
    if (urlQ !== null) setSearch(urlQ);
  }, [urlQ]);
  const q = useDebounced(search.trim(), 300);
  const [filter, setFilter] = useState<ThreadFilter | null>(null);
  // ?category= so sidebar links and the command palette can open a category directly.
  const category = params.get('category');
  const setCategory = useCallback(
    (key: string | null) => {
      const next = new URLSearchParams(params);
      if (key) next.set('category', key);
      else next.delete('category');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const categories = useCategoryMap();
  const list = useThreadList({ view: API_VIEW[view], mailboxId: accountId ?? undefined, folderId, q, filter: filter ?? undefined, category: category ?? undefined });
  const updateThread = useUpdateThread();
  const compact = useSettings()?.density === 'compact';
  const demoMode = useMe().data?.demoMode;
  const threads = useMemo(() => list.data?.pages.flatMap((p) => p.threads) ?? [], [list.data]);

  const folderMailbox = useMemo(() => (folderId ? accountId : null), [folderId, accountId]);
  const folders = useFolders(folderMailbox);
  const folderName = folders.data?.folders.find((f) => f.id === folderId)?.name;
  const title = view === 'folder' ? folderName ?? 'Folder' : TITLES[view];

  const base = view === 'folder' ? `/folder/${folderId}` : `/${view}`;
  const open = useCallback((id: string) => navigate(`${base}/${id}${location.search}`), [navigate, base, location.search]);
  const back = useCallback(() => navigate(`${base}${location.search}`), [navigate, base, location.search]);

  const list_ = useResizable('list', 400, 320, 600);
  const [drawer, setDrawer] = useState(false);
  // Details sheet inside the reading panel (remembered); on phones/tablets it's a drawer.
  const [sheet, setSheet] = useLocalStorage<boolean>('mp:details-sheet', false);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  useEffect(() => {
    setDetail(null);
    setDrawer(false);
  }, [threadId]);

  const searchRef = useRef<HTMLInputElement>(null);
  const viewRef = useRef<ThreadViewHandle>(null);
  const idx = threads.findIndex((t) => t.id === threadId);
  useHotkeys({
    j: () => {
      const next = threads[idx + 1] ?? (idx < 0 ? threads[0] : undefined);
      if (next) open(next.id);
      else if (list.hasNextPage) void list.fetchNextPage();
    },
    k: () => {
      const prev = threads[idx - 1];
      if (prev) open(prev.id);
    },
    '/': (e) => {
      e.preventDefault();
      searchRef.current?.focus();
    },
    r: (e) => {
      if (threadId) {
        e.preventDefault();
        viewRef.current?.reply();
      }
    },
    s: () => threadId && viewRef.current?.toggleFlag(),
    u: () => threadId && viewRef.current?.toggleUnread(),
    i: () => threadId && (bp === 'desktop' ? setSheet(!sheet) : setDrawer((d) => !d)),
    Escape: () => {
      if (drawer) setDrawer(false);
      else if (document.activeElement === searchRef.current) searchRef.current?.blur();
      else if (bp === 'mobile' && threadId) back();
    },
  });

  const noMailboxes = mbs.isSuccess && mailboxList.length === 0;
  const listEl = (
    <ThreadList
      ref={searchRef}
      view={view}
      title={title}
      threads={threads}
      selectedId={threadId}
      onOpen={open}
      onQuick={({ id, ...patch }) => updateThread.mutate({ id, ...patch })}
      mailboxes={mailboxMap}
      mailboxList={mailboxList}
      accountId={accountId}
      onAccount={setAccountId}
      search={search}
      onSearch={setSearch}
      filter={filter}
      onFilter={setFilter}
      categories={categories}
      category={category}
      onCategory={setCategory}
      compact={compact}
      isLoading={list.isPending || (list.isFetching && !list.isFetchingNextPage)}
      error={list.error}
      onRetry={() => void list.refetch()}
      hasMore={!!list.hasNextPage}
      loadingMore={list.isFetchingNextPage}
      onLoadMore={() => void list.fetchNextPage()}
      noMailboxes={noMailboxes}
      demoMode={demoMode}
      emptyText={EMPTY[view]}
    />
  );
  const contextEl = detail && detail.thread.id === threadId ? <ContextPanel detail={detail} mailboxes={mailboxList} /> : null;
  const viewEl = threadId ? (
    <ThreadView
      ref={viewRef}
      threadId={threadId}
      mailboxes={mailboxMap}
      onBack={bp === 'mobile' ? back : undefined}
      detailsOpen={bp === 'desktop' ? sheet : drawer}
      onToggleDetails={() => (bp === 'desktop' ? setSheet(!sheet) : setDrawer((d) => !d))}
      details={bp === 'desktop' ? contextEl : null}
      onLoaded={setDetail}
    />
  ) : (
    <EmptyState
      className="h-full"
      icon={<MailSearch className="h-6 w-6" />}
      title="Select a conversation"
      description={
        <span className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          <span className="flex items-center gap-1"><kbd>j</kbd><kbd>k</kbd> move</span>
          <span className="flex items-center gap-1"><kbd>/</kbd> search</span>
          <span className="flex items-center gap-1"><kbd>r</kbd> reply</span>
          <span className="flex items-center gap-1"><kbd>s</kbd> flag</span>
          <span className="flex items-center gap-1"><kbd>i</kbd> details</span>
        </span>
      }
    />
  );

  if (bp === 'mobile') {
    return (
      <div className="h-full">
        {threadId ? <div className="mp-panel mx-2 mt-2 h-[calc(100%-8px)] overflow-hidden">{viewEl}</div> : listEl}
        <Drawer open={drawer} onClose={() => setDrawer(false)} title="Conversation details">
          <div className="p-4">{contextEl}</div>
        </Drawer>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 p-3 pl-0 sm:pl-3">
      <section style={{ width: list_.width }} className="mp-panel flex h-full shrink-0 flex-col overflow-hidden">
        {listEl}
      </section>
      <Divider {...list_.handleProps} label="Resize message list" dragging={list_.dragging} />
      <section className="mp-panel relative h-full min-w-0 flex-1 overflow-hidden">{viewEl}</section>
      {bp === 'tablet' && (
        <Drawer open={drawer} onClose={() => setDrawer(false)} title="Conversation details">
          <div className="p-4">{contextEl}</div>
        </Drawer>
      )}
    </div>
  );
}
